///
/// Copyright © 2016-2026 The Thingsboard Authors
///
/// Licensed under the Apache License, Version 2.0 (the "License");
/// you may not use this file except in compliance with the License.
/// You may obtain a copy of the License at
///
///     http://www.apache.org/licenses/LICENSE-2.0
///
/// Unless required by applicable law or agreed to in writing, software
/// distributed under the License is distributed on an "AS IS" BASIS,
/// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
/// See the License for the specific language governing permissions and
/// limitations under the License.
///

import { EntityType } from '@shared/models/entity-type.models';

/**
 * Group permission for self-registration
 */
export interface GroupPermission {
  id?: string;
  userGroupId?: string;
  roleId?: string;
  entityGroupId?: string;
  entityGroupType?: EntityType;
  isPublic?: boolean;
}

/**
 * Public self-registration parameters displayed on signup form
 */
export interface SignUpSelfRegistrationParams {
  /** True when self-registration is actively configured for the requested domain —
   *  drives the sign-up link on the login page. */
  selfRegistrationEnabled?: boolean;
  signUpTextMessage?: string;
  captchaSiteKey?: string;
  showPrivacyPolicy?: boolean;
  showTermsOfUse?: boolean;
  captchaVersion?: 'v2' | 'v3';
  captchaActionName?: string;
  /** Whether the tenant requires email verification (activation link) before the account works. */
  emailVerificationRequired?: boolean;
  /** Mobile-app deeplink scheme (e.g. "farmprotec") — used by the web activation page to offer "open in app". */
  appScheme?: string;
  /** Mobile-app deeplink host (e.g. "app"), combined with appScheme. */
  appHost?: string;
  /** True when the login page should offer the native app instead of the web form on a mobile
   *  browser. Only ever true when the requested host matches the tenant's configured domain. */
  appLoginRedirectEnabled?: boolean;
  /** Deeplink path appended to appScheme://appHost; defaults to "/". */
  appLoginPath?: string;
  /** Optional store URL for a "get the app" link when the deeplink does not resolve. */
  appStoreUrl?: string;
}

/**
 * Full self-registration parameters including admin-only settings
 */
export interface SelfRegistrationParams extends SignUpSelfRegistrationParams {
  adminSettingsId?: string;
  domainName?: string;
  captchaSecretKey?: string;
  privacyPolicy?: string;
  termsOfUse?: string;
  notificationEmail?: string;
  defaultDashboardId?: string;
  defaultDashboardFullscreen?: boolean;
  permissions?: GroupPermission[];
  /** UserFunction tier stamped on each self-registered user (the "default role"). */
  defaultUserFunction?: string;
  emailVerificationRequired?: boolean;
  adminApprovalRequired?: boolean;
  pkgName?: string;
  appSecret?: string;
}

/**
 * The UserFunction tiers offered as the self-registration "default role".
 * CUSTOMER_ADMIN = full CRUD within the customer scope; CUSTOMER_USER / TENANT_USER = read-only.
 */
export const selfRegistrationRoleOptions: Array<{ value: string; label: string }> = [
  { value: 'CUSTOMER_ADMIN', label: 'self-registration.role-customer-admin' },
  { value: 'CUSTOMER_USER', label: 'self-registration.role-read-only' }
];

/**
 * Request for user self-registration
 */
export interface SignUpRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  recaptchaResponse?: string;
  pkgName?: string;
  appSecret?: string;
}

/**
 * Result of signup request
 */
export enum SignUpResult {
  SUCCESS = 'SUCCESS',
  INACTIVE_USER_EXISTS = 'INACTIVE_USER_EXISTS'
}

/**
 * JWT token pair returned after activation
 */
export interface JwtTokenPair {
  token: string;
  refreshToken: string;
}

/**
 * Signup response with result and optional message
 */
export interface SignUpResponse {
  result: SignUpResult;
  message?: string;
  userId?: string;
}

/**
 * Email activation request
 */
export interface ActivateByEmailCodeRequest {
  emailCode: string;
  password?: string;
}

/**
 * Privacy policy and terms acceptance status
 */
export interface PolicyAcceptanceStatus {
  privacyPolicyAccepted: boolean;
  termsOfUseAccepted: boolean;
  privacyPolicyAcceptedTime?: number;
  termsOfUseAcceptedTime?: number;
}
